/**
 * treeBuilderClient
 *
 * Purpose
 * - Provide a singleton Comlink client for the TreeBuilderWorker so all tree-build
 *   requests share the same worker instance.
 * - Centralizes the invocation surface and progress proxying.
 *
 * Notes
 * - The worker builds grid trees from model data stored in OPFS (Origin Private File System).
 * - Callers provide a small, serializable description of grouping fields to keep
 *   the worker side decoupled from UI concerns.
 * - Testing: Pass slowTestDelayMs > 0 in options to add delays for testing the LoadingOverlay UI.
 * - Set useMainThread: true in options to run on the main thread instead of a worker.
 */
import type { SerializableGroupingField, SerializableTreeBuilderOptions, TreeBuildProgressHandler, TreeBuildResult } from '@/workers/TreeBuilderWorker';
import { proxy } from 'comlink';

// Lazy, on-demand singleton to avoid spawning the worker at app startup
let _workerInstance: any | undefined;
function getWorkerSingleton() {
  if (!_workerInstance) {
    _workerInstance = new ComlinkWorker<typeof import('@/workers/TreeBuilderWorker')>(
      new URL('./TreeBuilderWorker', import.meta.url),
      { name: 'TreeBuilderWorker', type: 'module' }
    );
  }
  return _workerInstance;
}

/**
 * Build a tree from OPFS-stored model data.
 *
 * @param modelIds            Array of model GUIDs whose nodes should be merged.
 * @param serializableFields  Minimal, serializable grouping/labeling fields for the worker.
 * @param labelMap            Optional map (locale-aware labels, categories etc.) to enrich nodes.
 * @param onProgress          Optional callback for progress updates (proxied to worker via Comlink).
 * @param options             Optional tree builder options (e.g., preserveHierarchy, useMainThread).
 * @param scenarioId          Optional scenario ID to load supplemental nodes from IndexedDB.
 *
 * @returns TreeBuildResult   Flattened tree plus metadata produced by the worker.
 */
export async function buildTreeFromOPFS(
  modelIds: string[],
  serializableFields: SerializableGroupingField[],
  labelMap?: Record<string, Record<string, string>>,
  onProgress?: TreeBuildProgressHandler,
  options?: SerializableTreeBuilderOptions,
  scenarioId?: string
): Promise<TreeBuildResult> {
  const mode = options?.useMainThread ? 'MAIN_THREAD' : 'WEB_WORKER';
  const startTime = performance.now();

  if (options?.slowTestMode) {
    console.log(`[treeBuilderClient] Slow test mode enabled with ${options.slowTestDelayMs ?? 1500}ms delay`);
  }

  console.log(`[treeBuilderClient] 🚀 Starting tree build (mode: ${mode}, models: ${modelIds.length})`);

  let result: TreeBuildResult;

  // Run on main thread if requested (useful for debugging or when workers unavailable)
  if (options?.useMainThread) {
    const importStart = performance.now();
    const { buildTreeFromOPFS } = await import('@/workers/TreeBuilderWorker');
    const importTime = performance.now() - importStart;
    console.log(`[treeBuilderClient] [${mode}] Module import: ${importTime.toFixed(2)}ms`);

    result = await buildTreeFromOPFS(
      modelIds,
      serializableFields,
      labelMap,
      onProgress,
      options,
      scenarioId
    );
  } else {
    // Default: use web worker
    const worker = getWorkerSingleton();
    result = await (worker as any).buildTreeFromOPFS(
      modelIds,
      serializableFields,
      labelMap,
      onProgress ? proxy(onProgress) : undefined,
      options,
      scenarioId
    );
  }

  const totalTime = performance.now() - startTime;
  const workerTime = result.buildTimeMs;
  const overheadTime = totalTime - workerTime;

  // Performance summary
  console.log(`[treeBuilderClient] ✅ Tree build complete (mode: ${mode})`);
  console.log(`[treeBuilderClient] 📊 Performance Metrics:`);
  console.log(`  ├─ Mode:           ${mode}`);
  console.log(`  ├─ Models:         ${modelIds.length}`);
  console.log(`  ├─ Nodes:          ${result.nodeCount.toLocaleString()}`);
  console.log(`  ├─ Rows:           ${result.rows.length.toLocaleString()}`);
  console.log(`  ├─ Worker time:    ${workerTime.toFixed(2)}ms`);
  console.log(`  ├─ Overhead:       ${overheadTime.toFixed(2)}ms ${mode === 'WEB_WORKER' ? '(Comlink serialization)' : '(module import)'}`);
  console.log(`  └─ Total time:     ${totalTime.toFixed(2)}ms`);

  // Add a comparison-friendly single-line summary
  console.log(`[treeBuilderClient] 📈 SUMMARY [${mode}]: ${result.nodeCount} nodes → ${result.rows.length} rows in ${totalTime.toFixed(0)}ms (worker: ${workerTime.toFixed(0)}ms, overhead: ${overheadTime.toFixed(0)}ms)`);

  return result;
}

